import { useChat } from '@ai-sdk/react';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SendIcon from '@mui/icons-material/Send';
import { Alert, Box, Chip, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import {
    DefaultChatTransport,
    type UIDataTypes,
    type UIMessage,
    type UIMessagePart,
    type UITools,
    getToolName,
    isReasoningUIPart,
    isTextUIPart,
    isToolUIPart,
} from 'ai';
import { KeyboardEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { Resizable, type ResizeCallbackData } from 'react-resizable';
import remarkGfm from 'remark-gfm';

import { useApiToken } from '@/api/sofarApi';
import { CopyButton } from '@/components/CopyButton';
import { type ChatContext, describeChatContext, useChatContext } from '@/components/chat/useChatContext';
import { useChatOpener } from '@/components/chat/useChatOpener';
import { useSmoothedText } from '@/components/chat/useSmoothedText';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { CHAT_SERVER_URL } from '@/config';
import { useGlobalStore } from '@/store';

import 'react-resizable/css/styles.css';

type ChatPanelProps = {
    isOpen: boolean;
    onClose: () => void;
};

const DEFAULT_SIZE = { width: 480, height: 560 };
const MIN_SIZE: [number, number] = [320, 400];
const MAX_SIZE: [number, number] = [720, 880];

const LOADING_PHRASES = [
    'Hoisting the mainsail…',
    'Charting a course…',
    'Taking soundings…',
    'Trimming the sails…',
    'Reading the swell…',
    'Scanning the horizon…',
    'Weighing anchor…',
    'Consulting the compass…',
    'Watching for squalls…',
    'Casting the lead line…',
    'Battening down the hatches…',
    'Plotting a heading…',
    'Checking the barometer…',
    'Riding the current…',
    'Reading the tide tables…',
    'Adjusting for leeway…',
];

const LOADING_PHRASE_INTERVAL_MS = 3500;

const randomLoadingPhraseIndex = (excludeIndex: number): number => {
    if (LOADING_PHRASES.length <= 1) {
        return 0;
    }

    const index = Math.floor(Math.random() * (LOADING_PHRASES.length - 1));
    return index >= excludeIndex ? index + 1 : index;
};

const useLoadingPhrase = (active: boolean): string => {
    const [index, setIndex] = useState(() => Math.floor(Math.random() * LOADING_PHRASES.length));

    useEffect(() => {
        if (!active) {
            return;
        }

        const interval = setInterval(() => {
            setIndex((previous) => randomLoadingPhraseIndex(previous));
        }, LOADING_PHRASE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [active]);

    return LOADING_PHRASES[index];
};

const TOOL_LABELS: Record<string, string> = {
    get_device_transmission_status: 'Checking transmission status',
    get_device_connectivity: 'Checking connectivity',
    get_sound_levels: 'Checking sound levels',
    get_sound_level_baseline: 'Checking sound level baseline',
    get_sound_level_wave_correlation: 'Correlating sound levels with waves',
    get_latest_conditions: 'Checking latest conditions',
    get_wave_spectrum: 'Checking wave spectrum',
    get_wind_wave_correlation: 'Correlating wind and waves',
    get_nearby_ais_vessels: 'Checking nearby vessels',
    get_location_history: 'Checking location history',
    get_sensor_configuration: 'Checking sensor configuration',
    get_fleet_condition_comparison: 'Comparing fleet conditions',
    get_fleet_connectivity_status: 'Checking fleet connectivity',
    get_fleet_config_drift: 'Checking fleet configuration',
    get_detections: 'Checking detections',
    get_detection_counts_by_day: 'Checking detection counts',
    get_situation_report: 'Building situation report',
    get_current_speed_baseline: 'Checking current speed baseline',
    get_dissolved_oxygen_baseline: 'Checking dissolved oxygen baseline',
    get_temperature_baseline: 'Checking temperature baseline',
    list_devices: 'Listing devices',
    search_devices_by_area: 'Searching devices by area',
};

const friendlyToolLabel = (toolName: string): string =>
    TOOL_LABELS[toolName] ?? `Checking ${toolName.replace(/_/g, ' ')}`;

// wavefleet tokens aren't documented as expiring on a fixed schedule client-side, so this stays a
// conservative margin under useApiToken's own 5-minute staleTime - just enough to keep a
// long-running open conversation from quietly sitting on a token gone stale.
const TOKEN_REFETCH_INTERVAL_MS = 1000 * 60 * 4;

export const ChatPanel = ({ isOpen, onClose }: ChatPanelProps) => {
    const {
        data: apiTokenData,
        isPending: isTokenPending,
        isError: isTokenError,
    } = useApiToken({ config: { refetchInterval: isOpen ? TOKEN_REFETCH_INTERVAL_MS : false } });
    const wavefleetToken = apiTokenData?.data.token;

    // Read from a ref (updated after every render below) rather than closing over `chatContext`
    // directly, so a request mid-conversation reflects whatever page/device is current at send
    // time rather than whatever was current when this transport instance was built.
    const chatContext = useChatContext();
    const chatContextRef = useRef(chatContext);
    useEffect(() => {
        chatContextRef.current = chatContext;
    });

    // headers as a function (rather than a plain object) so it re-reads wavefleetToken at request
    // time instead of closing over whatever value was current when the transport was built.
    const transport = useMemo(
        () =>
            new DefaultChatTransport({
                api: `${CHAT_SERVER_URL}/chat/stream`,
                headers: (): Record<string, string> => (wavefleetToken ? { 'X-Wavefleet-Token': wavefleetToken } : {}),
                body: (): { context: ChatContext } => ({ context: chatContextRef.current }),
            }),
        [wavefleetToken]
    );

    const { messages, sendMessage, setMessages, status, error, clearError, regenerate } = useChat({ transport });
    const setHasUnreadChatMessage = useGlobalStore((state) => state.setHasUnreadChatMessage);
    const { isFetchingOpeningReply, consumeOpeningHistoryOptions, resetOpeningHistory } = useChatOpener({
        wavefleetToken,
        isOpen,
        setMessages,
    });

    const [inputValue, setInputValue] = useState('');
    const [size, setSize] = useState(DEFAULT_SIZE);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const previousStatusRef = useRef(status);

    const handleResize = (_event: SyntheticEvent, data: ResizeCallbackData) => {
        setSize({ width: data.size.width, height: data.size.height });
    };

    const isBusy = status === 'submitted' || status === 'streaming' || isFetchingOpeningReply;
    const lastMessage = messages[messages.length - 1];
    const showLoadingPhrase = shouldShowLoadingPhrase(lastMessage, isBusy);
    const loadingPhrase = useLoadingPhrase(showLoadingPhrase);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }, [messages, isBusy]);

    useEffect(() => {
        const wasBusy = previousStatusRef.current === 'submitted' || previousStatusRef.current === 'streaming';
        if (wasBusy && status === 'ready' && !isOpen) {
            setHasUnreadChatMessage(true);
        }
        previousStatusRef.current = status;
    }, [status, isOpen, setHasUnreadChatMessage]);

    const handleNewConversation = () => {
        setMessages([]);
        clearError();
        resetOpeningHistory();
    };

    const isRetryDisabled = !wavefleetToken || isBusy;

    const handleRetry = (messageId: string) => {
        if (isRetryDisabled) {
            return;
        }

        regenerate({ messageId });
    };

    const handleSend = () => {
        const text = inputValue.trim();
        if (!text || !wavefleetToken || isBusy) {
            return;
        }

        setInputValue('');

        const openingHistoryOptions = consumeOpeningHistoryOptions();
        if (openingHistoryOptions) {
            sendMessage({ text }, openingHistoryOptions);
            return;
        }

        sendMessage({ text });
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const isSendDisabled = !inputValue.trim() || !wavefleetToken || isBusy;

    if (!isOpen) {
        return null;
    }

    return (
        <Box sx={{ position: 'fixed', bottom: 96, right: 24, zIndex: 1300 }}>
            <Resizable
                width={size.width}
                height={size.height}
                onResize={handleResize}
                resizeHandles={['nw']}
                minConstraints={MIN_SIZE}
                maxConstraints={MAX_SIZE}
            >
                <Paper
                    elevation={8}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: size.width,
                        height: size.height,
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 3, py: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}
                    >
                        <Typography variant="h5" component="h2">
                            Spotter Assistant
                        </Typography>
                        <IconButton size="small" onClick={onClose} aria-label="Minimize chat" sx={{ color: 'inherit' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Box
                        sx={{ px: 3, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                    >
                        <Typography variant="caption" color="text.secondary" data-testid="chat-context-banner">
                            Viewing: {describeChatContext(chatContext)}
                        </Typography>
                    </Box>

                    <Stack sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2, gap: 2 }}>
                        {messages.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                Ask about device transmission status, sound levels, wave and wind conditions, sensor
                                configuration, or nearby vessels.
                            </Typography>
                        )}
                        {messages.map((message) => (
                            <ChatMessageBubble
                                key={message.id}
                                message={message}
                                onRetry={handleRetry}
                                isRetryDisabled={isRetryDisabled}
                            />
                        ))}
                        {showLoadingPhrase && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <LoadingIndicatorIcon isLoading />
                                <Typography variant="body2" color="text.secondary" data-testid="chat-typing-indicator">
                                    {loadingPhrase}
                                </Typography>
                            </Stack>
                        )}
                        <div ref={messagesEndRef} />
                    </Stack>

                    {(isTokenError || error) && (
                        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
                            {isTokenError
                                ? "Couldn't load your Sofar API token, so the assistant can't answer yet."
                                : (error?.message ?? 'Something went wrong. Please try again.')}
                        </Alert>
                    )}

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            size="small"
                            placeholder="Ask a question about your Spotters..."
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            disabled={isTokenPending || isTokenError}
                        />
                        <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={isSendDisabled}
                            aria-label="Send message"
                        >
                            <SendIcon />
                        </IconButton>
                    </Stack>
                    {messages.length > 0 && (
                        <Box sx={{ px: 3, pb: 1.5, textAlign: 'right' }}>
                            <Typography
                                variant="caption"
                                component="button"
                                onClick={handleNewConversation}
                                sx={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    color: 'text.secondary',
                                    textDecoration: 'underline',
                                    p: 0,
                                }}
                            >
                                Start new conversation
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Resizable>
        </Box>
    );
};

/**
 * Splits a message's parts into steps, delimited by the 'step-start' parts the ai sdk
 * inserts at the beginning of each step in call loop
 */
const groupPartsIntoSteps = (parts: UIMessagePart<UIDataTypes, UITools>[]): UIMessagePart<UIDataTypes, UITools>[][] => {
    const steps: UIMessagePart<UIDataTypes, UITools>[][] = [[]];
    parts.forEach((part) => {
        if (part.type === 'step-start') {
            steps.push([]);
            return;
        }
        steps[steps.length - 1].push(part);
    });
    return steps.filter((step) => step.length > 0);
};

const isToolPartFinished = (part: UIMessagePart<UIDataTypes, UITools>): boolean =>
    isToolUIPart(part) &&
    (part.state === 'output-available' || part.state === 'output-error' || part.state === 'output-denied');

const isReasoningPartFinished = (part: UIMessagePart<UIDataTypes, UITools>): boolean =>
    isReasoningUIPart(part) && part.state === 'done';

const isPartActive = (part: UIMessagePart<UIDataTypes, UITools>): boolean => {
    if (isTextUIPart(part)) {
        return true;
    }
    if (isToolUIPart(part)) {
        return !isToolPartFinished(part);
    }
    if (isReasoningUIPart(part)) {
        return !isReasoningPartFinished(part);
    }
    return false;
};

// Only the current step can hold the phrase back - earlier steps are already collapsed behind the
// reasoning toggle, so their narration shouldn't count as "still active" for the rest of the reply.
const shouldShowLoadingPhrase = (lastMessage: UIMessage | undefined, isBusy: boolean): boolean => {
    if (!isBusy) {
        return false;
    }
    if (lastMessage?.role !== 'assistant') {
        return true;
    }

    const currentStepParts = groupPartsIntoSteps(lastMessage.parts).at(-1) ?? [];
    return !currentStepParts.some(isPartActive);
};

type StepRenderItem =
    | { kind: 'part'; part: UIMessagePart<UIDataTypes, UITools> }
    | { kind: 'tool-group'; toolName: string; count: number; isFinished: boolean }
    | { kind: 'reasoning'; isFinished: boolean };

/**
 * Merges duplicate actions (parallel tool calls, multiple reasoning sections)in the same
 * step in order to render as one status. A merged group only counts as finished once
 * everything it represents has resolved.
 */
const groupStepParts = (parts: UIMessagePart<UIDataTypes, UITools>[]): StepRenderItem[] => {
    const items: StepRenderItem[] = [];
    const groupIndexByToolName = new Map<string, number>();
    let reasoningGroupIndex: number | undefined;

    parts.forEach((part) => {
        if (isReasoningUIPart(part)) {
            const isPartFinished = isReasoningPartFinished(part);

            if (reasoningGroupIndex === undefined) {
                reasoningGroupIndex = items.length;
                items.push({ kind: 'reasoning', isFinished: isPartFinished });
                return;
            }

            const reasoningGroup = items[reasoningGroupIndex];
            if (reasoningGroup.kind === 'reasoning') {
                reasoningGroup.isFinished = reasoningGroup.isFinished && isPartFinished;
            }
            return;
        }

        if (!isToolUIPart(part)) {
            items.push({ kind: 'part', part });
            return;
        }

        const toolName = getToolName(part);
        const isPartFinished = isToolPartFinished(part);
        const existingIndex = groupIndexByToolName.get(toolName);

        if (existingIndex === undefined) {
            groupIndexByToolName.set(toolName, items.length);
            items.push({ kind: 'tool-group', toolName, count: 1, isFinished: isPartFinished });
            return;
        }

        const group = items[existingIndex];
        if (group.kind === 'tool-group') {
            group.count += 1;
            group.isFinished = group.isFinished && isPartFinished;
        }
    });

    return items;
};

type ChatMessageBubbleProps = {
    message: UIMessage;
    onRetry: (messageId: string) => void;
    isRetryDisabled: boolean;
};

const ChatMessageBubble = ({ message, onRetry, isRetryDisabled }: ChatMessageBubbleProps) => {
    const isUser = message.role === 'user';
    const [showReasoning, setShowReasoning] = useState(false);

    const steps = useMemo(() => groupPartsIntoSteps(message.parts), [message.parts]);
    const reasoningSteps = isUser ? [] : steps.slice(0, -1);
    const finalStepParts = steps[steps.length - 1] ?? [];

    const renderItem = (item: StepRenderItem, key: string, isReasoningStep: boolean) => {
        if (item.kind === 'tool-group') {
            return (
                <ToolCallStatus
                    key={key}
                    toolName={item.toolName}
                    isFinished={item.isFinished}
                    isReasoningStep={isReasoningStep}
                />
            );
        }

        if (item.kind === 'reasoning') {
            return <ReasoningStatus key={key} isFinished={item.isFinished} isReasoningStep={isReasoningStep} />;
        }

        return (
            <ChatMessagePart
                key={key}
                part={item.part}
                isUser={isUser}
                onRetry={() => onRetry(message.id)}
                isRetryDisabled={isRetryDisabled}
            />
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                alignItems: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
            }}
        >
            {reasoningSteps.length > 0 && (
                <>
                    <Typography
                        variant="caption"
                        component="button"
                        onClick={() => setShowReasoning((previous) => !previous)}
                        aria-expanded={showReasoning}
                        data-testid="chat-reasoning-toggle"
                        sx={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            color: 'text.secondary',
                            textDecoration: 'underline',
                            p: 0,
                        }}
                    >
                        {showReasoning
                            ? 'Hide reasoning'
                            : `Show reasoning (${reasoningSteps.length} step${reasoningSteps.length === 1 ? '' : 's'})`}
                    </Typography>
                    {showReasoning &&
                        reasoningSteps.map((step, stepIndex) =>
                            groupStepParts(step).map((item, itemIndex) =>
                                renderItem(item, `reasoning-${stepIndex}-${itemIndex}`, true)
                            )
                        )}
                </>
            )}
            {groupStepParts(finalStepParts).map((item, index) => renderItem(item, `${index}`, false))}
        </Box>
    );
};

type ChatMessagePartProps = {
    part: UIMessagePart<UIDataTypes, UITools>;
    isUser: boolean;
    onRetry: () => void;
    isRetryDisabled: boolean;
};

const ChatMessagePart = ({ part, isUser, onRetry, isRetryDisabled }: ChatMessagePartProps) => {
    const displayedText = useSmoothedText(!isUser && isTextUIPart(part) ? part.text : '');

    if (isTextUIPart(part)) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    alignItems: isUser ? 'flex-end' : 'flex-start',
                    '&:hover .chat-message-actions, &:focus-within .chat-message-actions': { opacity: 1 },
                }}
            >
                <Box
                    sx={{
                        backgroundColor: isUser ? 'primary.main' : 'grey.100',
                        color: isUser ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        '& p': { m: 0 },
                        '& ul, & ol': { m: 0, pl: 3 },
                        '& table': {
                            display: 'block',
                            overflowX: 'auto',
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.8rem',
                        },
                        '& th, & td': {
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.5,
                            textAlign: 'left',
                        },
                        '& th': { backgroundColor: 'grey.200', fontWeight: 600 },
                    }}
                >
                    {isUser ? (
                        <Typography variant="body2">{part.text}</Typography>
                    ) : (
                        <Typography variant="body2" component="div">
                            <Markdown remarkPlugins={[remarkGfm]}>{displayedText}</Markdown>
                        </Typography>
                    )}
                </Box>
                <Stack
                    direction="row"
                    className="chat-message-actions"
                    sx={{
                        opacity: 0,
                        transition: 'opacity 0.15s',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 0,
                    }}
                >
                    <CopyButton
                        text={part.text}
                        aria-label="Copy message"
                        sx={{ my: 0, p: 0.25, borderRadius: 0, color: 'text.secondary' }}
                    />
                    <IconButton
                        onClick={onRetry}
                        disabled={isRetryDisabled}
                        aria-label="Retry message"
                        sx={{
                            p: 0.25,
                            borderRadius: 0,
                            color: 'text.secondary',
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <RestartAltIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>
        );
    }

    return null;
};

type ToolCallStatusProps = {
    toolName: string;
    isFinished: boolean;
    isReasoningStep: boolean;
};

const ToolCallStatus = ({ toolName, isFinished, isReasoningStep }: ToolCallStatusProps) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <LoadingIndicatorIcon isLoading={!isFinished && !isReasoningStep} />
        <Chip size="small" variant="outlined" label={friendlyToolLabel(toolName)} data-testid="chat-tool-status" />
    </Stack>
);

type ReasoningStatusProps = {
    isFinished: boolean;
    isReasoningStep: boolean;
};

const ReasoningStatus = ({ isFinished, isReasoningStep }: ReasoningStatusProps) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <LoadingIndicatorIcon isLoading={!isFinished && !isReasoningStep} />
        <Chip size="small" variant="outlined" label="Thinking…" data-testid="chat-reasoning-status" />
    </Stack>
);
